import { Component, OnDestroy, OnInit } from '@angular/core';
import { ColumnDef, CommonConstants, PageNotes, SortInfo, TableHeader } from '../../global/interfaces';
import { select, Store } from '@ngrx/store';
import { AppState } from '../../reducers';
import {
  CashFlowFileDownloadAction,
  LoadCashDataAction,
  LoadCashDatesAction,
  ResetCashFlowDataAction,
  SetCashFlowMonthFilterAction,
  SetCashFlowPeriodFilterAction,
  SetCashFlowQuarterFilterAction,
  SetCashFlowYearFilterAction,
  SetCashFlowSortAction,
  ResetCashFlowDataRecordsAction
} from './cash-flow.actions';
import {
  selectCountrySelected,
  selectMainFilterSelectedAccount,
  selectMainFilterSelectedClient,
  selectMainPageNotes,
  selectStaticData
} from '../../shared/layout/main.selectors';
import { distinctUntilChanged, filter, map, take, withLatestFrom } from 'rxjs/operators';
import { CashFlow, CashFlowRequest, Periods } from './cash-flow.models';
import { Subscription } from 'rxjs';
import {
  selectCashFlowData,
  selectCashFlowDate,
  selectCashFlowDatesMax,
  selectCashFlowDatesMin,
  selectCashFlowFilter,
  selectCashFlowFilterMonth,
  selectCashFlowFilterPeriod,
  selectCashFlowFilterQuarter,
  selectCashFlowFilterYear,
  selectCashFlowFrom,
  selectCashFlowLoading,
  selectCashFlowMonths,
  selectCashFlowQuarters,
  selectCashFlowTo,
  selectCashFlowYears
} from './cash-flow.selectors';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { Month, MONTHS, Quarter } from '../../global/dates';


import { getDataUri } from '../../global/getDataUri';
import { DecimalPipe } from '@angular/common';
import * as moment from 'moment';
import { isEqual, sortBy } from 'lodash';
import { WindowResizeService } from '../../global/window-resize.service';
import { convertDateForIE } from '../../shared/utils';
import * as pdfFonts from 'pdfmake/build/vfs_fonts';
import * as pdfMake from 'pdfmake/build/pdfmake';
import { georgiaFontFamily, georgiaFontVfs } from '../../shared/pdfs/georgia-font';
import { pdfFooter, pdfHeader, pdfStyle } from '../../shared/pdfs/templates';
import { User } from '../administration/user/user.models';
import { selectAuthUser } from '../auth/auth.selectors';
import { selectConsultants } from '../features.selectors';
import { LoadClientConsultantAction } from '../features.actions';
import { CONFIG } from '../../../config';
import { isNullOrUndefined } from 'util';
import { TableColumnAlignment } from 'atlas-table';
import { Actions, FieldSize } from 'atlas-cdk';
import { DropdownPlacement, DropdownTriggerType } from 'atlas-menu';
import { ToastService } from '../../shared/toaster_/toaster_.service';

@Component({
  standalone: false,
  selector: 'dsp-cash-flow',
  templateUrl: 'cash-flow.container.html',
  styleUrls: ['cash-flow.container.scss']
})
export class CashFlowContainerComponent implements OnInit, OnDestroy {
  dropdownTriggerTypeIconButton = DropdownTriggerType.IconButton;
  dropdownPlacementLeftStart = DropdownPlacement.LeftStart;
  sizeMedium = FieldSize.medium;
  sizeSmall = FieldSize.small;

  public headers: ColumnDef[] = [
    {

      displayLabel: 'Name',
      fieldKey: 'fund',
      sortable: true,
      column: 'fund'
    },
    {
      displayLabel: 'Opening Balance ($)',
      fieldKey: 'openingValue',
      align: TableColumnAlignment.right,
      sortable: true,
      column: 'openingValue'
    },
    {
      displayLabel: 'Net Cash Flow ($)',
      fieldKey: 'netCashFlow',
      align: TableColumnAlignment.right,
      sortable: true,
      column: 'netCashFlow',
    },
    {
      displayLabel: 'Net Investment Change ($)',
      fieldKey: 'netChange',
      align: TableColumnAlignment.right,
      sortable: true,
      column: 'netChange',
    },
    {
      displayLabel: 'Closing Balance ($)',
      fieldKey: 'closingValue',
      align: TableColumnAlignment.right,
      sortable: true,
      column: 'closingValue',
    }
  ];

  public filterTypes: string[] = ['select', 'select', 'select'];
  public mobileFilterTypes: string[] = ['select', 'datepicker'];
  public periodFilterOptions: { label: string; value: Periods }[] = [
    { label: 'Month', value: Periods.MONTH },
    { label: 'Quarter', value: Periods.QUARTER }
  ];

  public period: Periods;

  public month: number;

  public year: number;

  public quarter: number;

  public asAt: string;

  public loading$: Observable<boolean>;
  public years$: Observable<Number[]> = new Observable<Number[]>(null);
  public months$: Observable<Month[]>;
  public quarters$: Observable<Quarter[]>;
  public yearFilterOptions$: Observable<{ label: number; value: number }[]>;
  public monthFilterOptions$: Observable<{ label: string; value: number }[]>;
  public quarterFilterOptions$: Observable<{ label: string; value: number }[]>;
  public minDate$: Observable<string | undefined>;
  public maxDate$: Observable<string | undefined>;
  public date$: Observable<string>;
  public data$: Observable<CashFlow[]>;
  public sortInfo$: BehaviorSubject<SortInfo> = new BehaviorSubject({ column: 'fund', reverse: false });
  public isMobile$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public filterTypes$: BehaviorSubject<string[]> = new BehaviorSubject(this.filterTypes);
  public notes$: BehaviorSubject<PageNotes> = new BehaviorSubject<PageNotes>({} as PageNotes);
  public user$: Observable<User | undefined>;
  public country = '';
  public account: string;

  private readonly subscription: Subscription = new Subscription();

  public constructor(

    private readonly store: Store<AppState>,
    private readonly decimalPipe: DecimalPipe,
    private readonly resize: WindowResizeService,
    private readonly _toasterService: ToastService
  ) {
    this.loading$ = this.selectLoading();
    this.years$ = this.selectYears();
    this.months$ = this.selectMonths();
    this.quarters$ = this.selectQuarters();
    this.yearFilterOptions$ = this.years$.pipe(
      map(years => years.map(year => ({ label: year as number, value: year as number })))
    );
    this.monthFilterOptions$ = this.months$.pipe(
      map(months => months.map(month => ({ label: month.name, value: month.value })))
    );
    this.quarterFilterOptions$ = this.quarters$.pipe(
      map(quarters => quarters.map(quarter => ({ label: quarter.name, value: quarter.value })))
    );
    this.minDate$ = this.selectMinDate();
    this.maxDate$ = this.selectMaxDate();
    this.date$ = this.selectDate();
    this.data$ = this.selectDisplayData();
    this.user$ = this.store.pipe(select(selectAuthUser));

  }

  public ngOnInit(): void {
    this.subscription.add(this.subscribeSelectedCountry());
    this.subscription.add(this.subscribeLoadCashFlowDates());
    this.subscription.add(this.subscribeLoadCashFlow());
    this.subscription.add(this.subscribePeriodFilter());
    this.subscription.add(this.subscribeInitialYearFilter());
    this.subscription.add(this.subscribeYearFilter());
    this.subscription.add(this.subscribeInitialMonthFilter());
    this.subscription.add(this.subscribeMonthFilter());
    this.subscription.add(this.subscribeInitialQuarterFilter());
    this.subscription.add(this.subscribeQuarterFilter());
    this.subscription.add(this.subscribeAsAtFilter());
    this.subscription.add(this.subscribeResetData());
    this.subscription.add(this.subscribeIsMobile());
    this.subscription.add(this.subscribeFilterTypes());
    this.subscription.add(this.subscribeNotes());
    this.subscription.add(this.subscribeConsultantLoad());

  }
  public subscribeSelectedCountry(): Subscription {
    return this.store.pipe(
      select(selectCountrySelected)
    ).subscribe(country => {
      this.country = country;
    });
  }
  public ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  public onSort(name, direction): void {
    let sortObj: SortInfo = { column: '', reverse: false };
    if (direction && name) {
      sortObj.column = name;
      if (direction === 'initial' || direction === 'asc') {
        sortObj.reverse = false;
      }
      if (direction === 'desc') {
        sortObj.reverse = true;
      }
      this.sortInfo$.next(sortObj);
    }
  }

  public onChangePeriod(event: Periods): void {
    if (event && (event === Periods.MONTH || event === Periods.QUARTER)) {
      this.store.dispatch(new SetCashFlowPeriodFilterAction(event));
    }
  }

  private subscribeConsultantLoad(): Subscription {
    return this.user$.subscribe(user => {
      if (user) {
        this.store.pipe(select(selectMainFilterSelectedClient)).subscribe(client => {
          if (!isNullOrUndefined(client)) {
            this.store.dispatch(new LoadClientConsultantAction(client._id));
          }
        });
      }
    });
  }

  public onChangeYear(event: number): void {

    if (event) {

      this.store.dispatch(new SetCashFlowYearFilterAction(event));

      this.store.pipe(
        select(selectCashFlowMonths),
        take(1)
      ).subscribe(months => {
        if (!isNullOrUndefined(months) && months.length > 0) {
          this.store.dispatch(new SetCashFlowMonthFilterAction(months[months.length - 1].value));
        }
      });

      this.store.pipe(
        select(selectCashFlowQuarters),
        take(1)
      ).subscribe(quarters => {
        if (!isNullOrUndefined(quarters) && quarters.length > 0) {
          this.store.dispatch(new SetCashFlowQuarterFilterAction(quarters[quarters.length - 1].value));
        }
      });
    }
  }

  public onChangeMonth(event: number): void {

    if (event !== null && event !== undefined) {

      this.store.dispatch(new SetCashFlowMonthFilterAction(event));

    }
  }

  /**
   * Used on mobile in place of Year and Month/Quarter filters
   * Updates Month, Quarter and Year if they don't match values in store
   */
  public onChangeAsAt(event: string): void {
    this.store.pipe(
      select(selectCashFlowFilter),
      take(1)
    ).subscribe(filter => {
      if (event !== null) {
        const date = moment.utc(event),
          quarter = date.quarter(),
          month = date.month(),
          year = date.year();

        if (filter.year !== year) {
          this.store.dispatch(new SetCashFlowYearFilterAction(year));
        }

        if (filter.month !== month) {
          this.store.dispatch(new SetCashFlowMonthFilterAction(month));
        }

        if (filter.quarter !== quarter) {
          this.store.dispatch(new SetCashFlowQuarterFilterAction(quarter));
        }
      }
    });
  }

  public onExportCSV(): void {
    // combineLatest(
    //   this.store.pipe(select(selectMainFilterSelectedAccount)),
    //   this.store.pipe(select(selectCashFlowFrom)),
    //   this.store.pipe(select(selectCashFlowTo)),
    //   this.data$
    // ).pipe(
    combineLatest([
      this.store.pipe(select(selectMainFilterSelectedAccount)),
      this.store.pipe(select(selectCashFlowFrom)),
      this.store.pipe(select(selectCashFlowTo)),
      this.data$]
    ).pipe(
      filter(([account]) => !!account),
      map(([account, from, to, data]) =>
      ({
        id: account._id, from, to, data, quarter: this.quarter,

        period: this.period === 'month' ? 'Monthly' : 'Quarterly'

      }
      )),
      take(1)
    ).subscribe(payload => {
      if (payload.data.length > 0) {
        // @ts-ignore
        payload = { ...payload, country: this.country };
        this.store.dispatch(new CashFlowFileDownloadAction(payload as CashFlowRequest));
      } else {
        //this.store.dispatch(new AddErrorToastAction(`There is no data in specified ${this.period}.`));
        return this._toasterService.launchAlertError(`There is no data in specified ${this.period}.`, Actions.error);
      }
    });
  }

  public onExportPDF(): void {
    // combineLatest(
    //   this.store.pipe(select(selectMainFilterSelectedClient)),
    //   this.store.pipe(select(selectMainFilterSelectedAccount)),
    //   this.data$,
    //   this.store.pipe(select(selectCashFlowFilter)),
    //   this.store.pipe(select(selectStaticData)),
    //   this.store.pipe(select(selectMainPageNotes)),
    //   this.store.pipe(select(selectConsultants))
    // ).pipe(
    combineLatest([
      this.store.pipe(select(selectMainFilterSelectedClient)),
      this.store.pipe(select(selectMainFilterSelectedAccount)),
      this.data$,
      this.store.pipe(select(selectCashFlowFilter)),
      this.store.pipe(select(selectStaticData)),
      this.store.pipe(select(selectMainPageNotes)),
      this.store.pipe(select(selectConsultants))]
    ).pipe(
      filter(([client, account]) => !!account),
      map(([client, account, data, filter, staticData, pageNotes, consultants]) =>
      ({
        client: client.name,
        accountName: account.name,
        accountNumber: account.number,
        data,
        period: filter.period,
        year: filter.year,
        month: filter.month,
        quarter: filter.quarter,
        staticData,
        pageNotes,
        consultants,
        country: this.country
      }
      )),
      take(1)
    ).subscribe(payload => {
      const mmcLogo = [],
        mercerLogo = [],
        icons = [],

        promises = [];
      promises.push(getDataUri('assets/images/mmc_logo.png', CommonConstants.pdfMmcLogoWidth, CommonConstants.pdfMmcLogoHeight)
        .then(data => mmcLogo.push(data)));
      promises.push(getDataUri('assets/images/mercer_brand_logo.png',
        CommonConstants.pdfMercerLogoWidth, CommonConstants.pdfMercerLogoHeight)
        .then(data => mercerLogo.push(data)));
      promises.push(getDataUri('assets/images/phone.png', 18, 18)
        .then(data => icons['phone'] = data));
      promises.push(getDataUri('assets/images/email.png', 18, 18)
        .then(data => icons['email'] = data));
      promises.push(getDataUri('assets/images/website.png', 18, 18)
        .then(data => icons['website'] = data));
      if (payload.data.length > 0) {
        const body = payload.data.map(data =>
        ([
          { text: data.fund || '-', style: 'textCell' },
          { text: data.openingValue ? this.decimalPipe.transform(data.openingValue, '1.0-4') : '-', style: 'numberCell' },
          { text: data.netCashFlow ? this.decimalPipe.transform(data.netCashFlow, '1.0-4') : '-', style: 'numberCell' },
          { text: data.netChange ? this.decimalPipe.transform(data.netChange, '1.0-4') : '-', style: 'numberCell' },
          { text: data.closingValue ? this.decimalPipe.transform(data.closingValue, '1.0-4') : '-', style: 'numberCell' }
        ]
        ));

        setTimeout(() => {
          Promise.all(promises).then(() => {
            const title = payload.period === 'month' ? 'Cash Flow Summary Monthly Report' : 'Cash Flow Summary Quarterly Report',
              date = ((payload.period === 'month' ? MONTHS[payload.month].name : 'Q' + (payload.quarter)) + ' ' + payload.year)
                .toUpperCase(),
              address = [];
            address.push(
              {
                text: !isNullOrUndefined(payload.staticData.entityName) ?
                  `${payload.staticData.entityName}` : '', style: 'clientTitle', pageBreak: 'before'
              },
              {
                text: !isNullOrUndefined(payload.staticData.address) ?
                  `${payload.staticData.address}` : '', style: 'clientAddress'
              },
            );
            const docDefinition = {
              content: [
                { text: title, style: 'title' },
                { text: date, style: 'subtitle' },
                { text: `${payload.client} - ${payload.accountName} - ${payload.accountNumber}`, style: 'subsubtitle' },
                { text: 'Date Generated: ' + moment().format(CommonConstants.displayDateFormatInAU), style: 'subtitleDate' },
                { text: 'Cash Flow Summary', style: 'chartTitleNew' },
                {
                  text: payload.period === 'month'
                    ? `as at end of ${moment.utc(convertDateForIE(date)).format('MMM YYYY')}`
                    : `as at end of Quarter ${payload.quarter} ${payload.year}`, style: 'subChartTitleNew'
                },
                {
                  table: {
                    headerRows: 1,
                    widths: ['*', 'auto', 'auto', 'auto', 'auto'],
                    body: [
                      [
                        { text: 'FUND NAME', style: 'tableHeader' },
                        { text: 'OPENING BALANCE ($)', style: 'tableHeader' },
                        { text: 'NET CASH FLOW ($)', style: 'tableHeader' },
                        { text: 'NET INVESTMENT CHANGE ($)', style: 'tableHeader' },
                        { text: 'CLOSING BALANCE ($)', style: 'tableHeader' }
                      ],
                      ...body
                    ]
                  },
                  layout: {
                    hLineWidth: function (i, node) {
                      return 1;

                    },
                    vLineWidth: function (i, node) {
                      return 1;
                    },
                    hLineColor: function (i, node) {
                      return 'white';
                    },
                    vLineColor: function (i, node) {
                      return 'white';

                    },
                  }
                },
                { text: '* Cash Flow Summary calculated utilizing Redemption / Sell Price', color: '#000f47', style: 'footnote' },
                { text: CommonConstants.notes, style: 'notesTitle', pageBreak: 'before' },
                { text: `${payload.pageNotes.cashflow.replace(/<br\/>/g, '\n')}`, style: 'notesData', pageBreak: 'after' },
                ...payload.consultants.map(consultant =>
                ([
                  { text: `${consultant.firstName} ${consultant.lastName}` || '', style: 'consultantName' },
                  {
                    columns: [
                      // { image: icons['phone'], width: 15, height: 11, margin: [0, 0, 0, 10] },
                      { text: `${consultant.phone}` || '', style: 'consultantPhone' }
                    ]
                  },
                  {
                    columns: [
                      // { image: icons['email'], width: 15, height: 11, margin: [0, 0, 0, 10] },
                      { text: `${consultant.username}` || '', style: 'consultantMail' },
                    ]
                  },
                ])
                ),
                { text: 'Mercer Multi Manager Fund Contact Details', style: 'fundContactTitle' },
                {
                  columns: [
                    // { image: icons['phone'], width: 15, height: 11, margin: [0, 0, 0, 10] },
                    { text: `${payload.staticData.phone || ''}`, style: 'fundContactPhone' }
                  ]
                },
                {
                  columns: [
                    // { image: icons['email'], width: 15, height: 11, margin: [0, 0, 0, 10] },
                    { text: `${payload.staticData.email || ''}`, style: 'fundContactEmail' }
                  ]
                },
                {
                  columns: [
                    // { image: icons['website'], width: 15, height: 11, margin: [0, 0, 0, 10] },
                    { text: `${payload.staticData.website || ''}`, style: 'fundContactWebsite' }
                  ]
                },
                { text: CommonConstants.importantNotices, style: 'noticeTitle', pageBreak: 'before' },
                { text: payload.staticData.importantNotices, style: 'noticeData' },
                ...address
              ],
              styles: {
                ...pdfStyle,
                footnote: {
                  fontSize: 9,
                  margin: [0, 10, 0, 0]
                }
              },
              footer(currentPage: number, totalPages: number) {
                return pdfFooter(currentPage, totalPages, payload, mmcLogo[0]);
              },
              header(currentPage: number, totalPages: number) {
                return pdfHeader(mmcLogo, title, currentPage, totalPages);
              },
              pageMargins: [40, 90, 40, 100]
            };

            pdfMake.vfs = {
              ...(pdfFonts.pdfMake as any).vfs,
              ...georgiaFontVfs
            };
            pdfMake.fonts = {
              ...georgiaFontFamily,
              Roboto: {
                normal: 'Roboto-Regular.ttf',
                bold: 'Roboto-Medium.ttf',
                italics: 'Roboto-Italic.ttf',
                bolditalics: 'Roboto-MediumItalic.ttf'
              }
            };
            pdfMake.createPdf(docDefinition).download('CashFlowSummaryExport.pdf');
          });
        }, 1000);
      } else {
        //this.store.dispatch(new AddErrorToastAction(`There is no data in specified ${this.period}.`));
        return this._toasterService.launchAlertError(`There is no data in specified ${this.period}.`, Actions.error);
      }
    });
  }

  public onChangeQuarter(event: number): void {

    if (event) {
      this.store.dispatch(new SetCashFlowQuarterFilterAction(event));
    }


  }

  private selectLoading(): Observable<boolean> {
    return this.store.pipe(
      select(selectCashFlowLoading)
    );
  }

  private selectYears(): Observable<number[]> {
    return this.store.pipe(
      select(selectCashFlowYears),
      distinctUntilChanged(isEqual)
    );
  }

  private selectMonths(): Observable<Month[]> {
    return this.store.pipe(
      select(selectCashFlowMonths)
    );
  }

  private selectQuarters(): Observable<Quarter[]> {
    return this.store.pipe(
      select(selectCashFlowQuarters)
    );
  }

  private selectMinDate(): Observable<string | undefined> {
    return this.store.pipe(
      select(selectCashFlowDatesMin),
      map(min => min ? min.utc().format(CommonConstants.displayDateFormatInAU) : undefined)
    );
  }

  private selectMaxDate(): Observable<string | undefined> {
    return this.store.pipe(
      select(selectCashFlowDatesMax),
      map(max => max ? max.utc().format(CommonConstants.displayDateFormatInAU) : undefined)
    );
  }

  private selectDate(): Observable<string> {
    return this.store.pipe(
      select(selectCashFlowDate),
    );
  }

  private selectDisplayData(): Observable<CashFlow[]> {
    // return combineLatest(
    //   this.store.pipe(select(selectCashFlowData)),
    //   this.sortInfo$
    // ).pipe(
    return combineLatest([
      this.store.pipe(select(selectCashFlowData)),
      this.sortInfo$]
    ).pipe(
      map(([data, sort]) => sort.reverse
        ? this.sortCashFlow(data, sort).reverse()
        : this.sortCashFlow(data, sort)
      )
    );
  }

  private sortCashFlow(data: CashFlow[], sort: SortInfo): CashFlow[] {
    switch (sort.column) {
      case 'openingValue':
      case 'closingValue':
      case 'netCashFlow':
      case 'netChange':
        const column = sort.column as keyof CashFlow;
        return sortBy(data, item => +item[column]);
      default:
        return sortBy(data, sort.column);
    }
  }

  private subscribeLoadCashFlowDates(): Subscription {
    return this.store.pipe(
      select(selectMainFilterSelectedAccount),
      distinctUntilChanged(isEqual)
    ).subscribe(account => {
      if (!!account) {
        this.store.dispatch(new ResetCashFlowDataAction());
        this.store.dispatch(new LoadCashDatesAction({ account: account._id }));
        this.account = account._id;
      }
    });
  }

  private subscribeLoadCashFlow(): Subscription {
    // return combineLatest(
    //   this.store.pipe(select(selectCashFlowFrom)),
    //   this.store.pipe(select(selectCashFlowTo)),
    // ).pipe(
    return combineLatest([
      this.store.pipe(select(selectCashFlowFrom)),
      this.store.pipe(select(selectCashFlowTo))]
    ).pipe(
      distinctUntilChanged(isEqual)
    ).subscribe(([from, to]) => {
      if (!!this.account && !!from && !!to) {
        this.store.dispatch(new LoadCashDataAction({
          id: this.account || '',
          from: from || '',
          to: to || '',
          country: this.country
        }));
      }
    });
  }

  private subscribePeriodFilter(): Subscription {
    return this.store.pipe(
      select(selectCashFlowFilterPeriod)
    ).subscribe(period => {
      this.period = period;

    });
  }

  private subscribeInitialYearFilter(): Subscription {
    // return combineLatest(
    //   this.store.pipe(select(selectCashFlowYears)),
    //   this.store.pipe(select(selectCashFlowFilterYear))
    // ).pipe(
    return combineLatest([
      this.store.pipe(select(selectCashFlowYears)),
      this.store.pipe(select(selectCashFlowFilterYear))]
    ).pipe(
      distinctUntilChanged(isEqual),
      map(([years, year]) => {
        if (!isNullOrUndefined(years) && years.length === 0) {
          this.year = undefined;

          this.store.dispatch(new ResetCashFlowDataRecordsAction());
        }
        return year !== undefined && years.indexOf(year) > -1 ? year : years[0];
      })
    ).subscribe(year => {
      this.store.dispatch(new SetCashFlowYearFilterAction(year));
    });
  }

  private subscribeYearFilter(): Subscription {
    return this.store.pipe(
      select(selectCashFlowFilterYear)
    ).subscribe(year => {
      this.year = year !== undefined ? year : undefined;

    });
  }

  private subscribeInitialMonthFilter(): Subscription {
    // return combineLatest(
    //   this.store.pipe(select(selectCashFlowMonths)),
    //   this.store.pipe(select(selectCashFlowFilterMonth))
    // ).pipe(
    return combineLatest([
      this.store.pipe(select(selectCashFlowMonths)),
      this.store.pipe(select(selectCashFlowFilterMonth))]
    ).pipe(
      filter(([months, month]) => !!months.length && month === undefined),
      map(([months, month]) => months[months.length - 1].value)
    ).subscribe(month => {
      this.store.dispatch(new SetCashFlowMonthFilterAction(month));
    });
  }

  private subscribeMonthFilter(): Subscription {
    return this.store.pipe(
      select(selectCashFlowFilterMonth)
    ).subscribe(month => {
      this.month = month !== undefined ? month : undefined;
    });
  }

  private subscribeInitialQuarterFilter(): Subscription {
    // return combineLatest(
    //   this.store.pipe(select(selectCashFlowQuarters)),
    //   this.store.pipe(select(selectCashFlowFilterQuarter))
    // ).pipe(
    return combineLatest([
      this.store.pipe(select(selectCashFlowQuarters)),
      this.store.pipe(select(selectCashFlowFilterQuarter))]
    ).pipe(
      filter(([quarters, quarter]) => !!quarters.length && quarter === undefined),
      map(([quarters, quarter]) => quarter !== undefined ? quarter : quarters[quarters.length - 1].value)
    ).subscribe(quarter => {
      this.store.dispatch(new SetCashFlowQuarterFilterAction(quarter));
    });
  }

  private subscribeQuarterFilter(): Subscription {
    return this.store.pipe(
      select(selectCashFlowFilterQuarter)
    ).subscribe(quarter => {
      this.quarter = quarter !== undefined ? quarter : undefined;

    });
  }

  /**
   * Sets asAt filter value when Max dates reload
   * default setting is latest available date (max)
   */
  private subscribeAsAtFilter(): Subscription {
    return this.store.pipe(
      select(selectCashFlowDatesMax),
      filter(max => max !== undefined)
    ).subscribe(max => {
      this.asAt = max.utc().format();
    });
  }

  private subscribeResetData(): Subscription {
    return this.store.pipe(
      select(selectMainFilterSelectedAccount),
      distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b))
    ).subscribe(account => {
      if (!account) {
        this.store.dispatch(new ResetCashFlowDataAction());
      }
    });
  }

  private subscribeIsMobile(): Subscription {
    return this.resize.mobile$.subscribe(this.isMobile$);
  }

  private subscribeFilterTypes(): Subscription {
    return this.isMobile$.pipe(
      map(isMobile => !isMobile ? this.filterTypes : this.mobileFilterTypes)
    ).subscribe(this.filterTypes$);
  }

  private subscribeNotes(): Subscription {
    return this.store.pipe(
      select(selectMainPageNotes)
    ).subscribe(this.notes$);
  }
}
